import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Badge,
  Alert,
  Form,
} from "react-bootstrap";
import theme from "../../styles/theme/theme";
import { capitalizeFirst } from "../../utils/stringUtils";
import { useDispatch, useSelector } from "react-redux";
import {
  createCheckoutSession,
  createTpiRequest,
  fetchShippingDetails,
  getOrderSummary,
} from "../../features/buyer/buyerSlice";
import Loader from "../../components/common/Loader";
import { FaBoxOpen, FaHandshake, FaTruck } from "react-icons/fa";
import { formatCurrency } from "../../utils/price";

const OrderSummary = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const {
    paymentLoading,
    orderLoading,
    orderError,
    orderSummary,
    shippingDetails,
    shippingDetailsLoading,
    shippingDetailsError,
    shippingDetailsSuccess,
  } = useSelector((state) => state.buyer);
  const shippingData = shippingDetails;
  const shippingMode = shippingData?.shipping_mode;

  const otpTestData = location.state?.otpTestData;
  const otp = otpTestData?.otp;
  const otpVerificationUrl = otpTestData?.otpVerificationUrl;
  const CURRENCY = "A$";

  const TermsAndConditionsURL =
    "https://scraptrade.com.au/pages/slug/terms-and-conditions-scraptrade-com-au";

  const [tpiRequested, setTpiRequested] = useState(false);
  const [tpiLoading, setTpiLoading] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  console.log("otp Test Data:", otp);
  console.log("Order Summary Shipping:", shippingData);
  console.log("Order Summary Data:", orderSummary);

  useEffect(() => {
    if (id) {
      dispatch(getOrderSummary(id));
      dispatch(fetchShippingDetails(id));
    }
  }, [id, dispatch]);

  const handleProceed = async () => {
    try {
      const payload = {
        order_id: orderSummary.order_id,
        amount: orderSummary.order_price_summary.total_with_tax,
      };

      const result = await dispatch(createCheckoutSession(payload)).unwrap();
      // console.log('Result url:',result.url)
      if (result?.url) {
        window.location.href = result.url;
      } else {
        alert("No redirect URL provided. Please contact support.");
      }
    } catch (err) {
      console.error("Checkout session creation failed:", err);
      alert(
        err?.message || "Failed to create checkout session. Please try again."
      );
    }
  };

  const handleTpiRequest = async () => {
    if (tpiRequested) return;

    try {
      setTpiLoading(true);

      await dispatch(createTpiRequest(orderSummary.order_id)).unwrap();

      setTpiRequested(true);
    } catch (err) {
      alert(err?.message || "Failed to create TPI request");
    } finally {
      setTpiLoading(false);
    }
  };

  if (orderLoading) return <Loader />;
  if (orderError)
    return (
      <Alert variant="danger" className="m-4">
        {orderError}
      </Alert>
    );
  if (!orderSummary) return null;

  const {
    order_id,
    created_at,
    status,
    product_summary,
    extra_delivery,
    order_price_summary,
    shipping_address,
    billing_address,
  } = orderSummary;

  const totalPayable =
    Number(order_price_summary?.final_bid_price || 0) +
    Number(order_price_summary?.tax_amount || 0);

  console.log("payload data:", order_id, totalPayable);

  const areAddressesSame =
    JSON.stringify(shipping_address) === JSON.stringify(billing_address);

  const formatShippingMode = (mode) => {
    switch (mode) {
      case "self_pickup":
        return "Self Pickup";
      case "seller_ship":
        return "Seller Ship";
      case "third_party":
        return "Third Party";
      default:
        return "N/A";
    }
  };

  return (
    <Container className="py-4 px-3 px-md-5">
      <div style={sectionCard}>
        <h3 style={{ color: theme.palette.primary.main }}>
          Confirm Your Order Summary
        </h3>
        <p style={{ fontSize: "0.95rem", marginBottom: 0 }}>
          Order ID: <strong>{order_id}</strong>
        </p>
        <p style={{ fontSize: "0.9rem", color: "#666" }}>
          Created on: {created_at}
        </p>
        <Badge bg={getStatusColor(status)} style={{ marginTop: 8 }}>
          {capitalizeFirst(status)}
        </Badge>
      </div>

      <Alert variant="info">
        Please review the summary and confirm to proceed.
      </Alert>

      {/* Product Summary */}
      <div style={sectionCard}>
        <h5 style={sectionTitle}>Product Summary</h5>
        <div style={infoRow}>
          <strong>Name:</strong> {product_summary?.name}
        </div>
        <div style={infoRow}>
          <strong>Location:</strong> {product_summary?.location}
        </div>
        <div style={infoRow}>
          <strong>Lead Time:</strong> {product_summary?.lead_time_days} days
        </div>
        <div style={infoRow}>
          <strong>Shipping Duration:</strong>{" "}
          {product_summary?.shipping_duration_days} days
        </div>
        <div style={infoRow}>
          <strong>Estimated Delivery:</strong>{" "}
          {product_summary?.estimated_delivery_date}
        </div>
        <div style={infoRow}>
          <strong>Incoterms:</strong> {product_summary?.incoterms}
        </div>
        <div style={infoRow}>
          <strong>Payment Terms:</strong> {product_summary?.payment_terms}
        </div>
      </div>

      {/* Shipping Details */}
      <div style={sectionCard}>
        <SectionHeaderWithMeta title="Shipping Details" mode={shippingMode} />

        {otpVerificationUrl && (
          <>
            <SectionDivider />

            <Alert variant="warning">
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                TEST MODE – OTP Verification
              </div>

              {otp && (
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                    marginBottom: 6,
                  }}
                >
                  <div style={{ minWidth: 140, fontWeight: 500 }}>OTP:</div>
                  <div>{otp}</div>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                }}
              >
                <div style={{ minWidth: 140, fontWeight: 500 }}>
                  Verification URL:
                </div>
                <div>
                  <a
                    href={otpVerificationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open OTP Verification Page
                  </a>
                </div>
              </div>
            </Alert>
          </>
        )}

        <SectionDivider />

        {shippingDetailsLoading && <Loader />}

        {shippingDetailsError && (
          <Alert variant="danger">{shippingDetailsError}</Alert>
        )}

        {shippingData && (
          <>
            {/* SELF PICKUP */}
            {shippingMode === "self_pickup" && (
              <>
                <AddressBlock
                  title="Pickup Address"
                  address={shippingData.pickup_address}
                  datePrefix="Pickup"
                  timeSlotLabel="Pickup Time Slot"
                  timeSlot={shippingData.pickup_address?.pickup_time_slot}
                  notesLabel="Pickup Notes"
                  notes={shippingData.pickup_address?.pickup_notes}
                  locationTypeLabel="Location Type"
                  locationType={shippingData?.pickup_address?.location_type}
                  forkliftLabel="Forklift Available"
                  forklift={shippingData?.pickup_address?.forklift_available}
                  assistanceLabel="Assistance Required"
                  assistance={shippingData?.pickup_address?.assistance_required}
                  startDate={shippingData?.pickup_address?.start_date}
                  endDate={shippingData?.pickup_address?.end_date}
                />

                <SectionDivider />

                <VehicleInfoBlock vehicle={shippingData.vehicle_info} />
                <DriverInfoBlock driver={shippingData.driver_info} />

                {(shippingData.confirmation_number ||
                  shippingData.carrier ||
                  shippingData.con_note) && <SectionDivider />}

                <Field
                  label="Confirmation Number"
                  value={shippingData.confirmation_number}
                />
                <Field label="Carrier" value={shippingData.carrier} />
                <Field label="Consignment Note" value={shippingData.con_note} />
              </>
            )}

            {/* SELLER SHIP */}
            {shippingMode === "seller_ship" && (
              <>
                <AddressBlock
                  title="Delivery Address"
                  address={shippingData.delivery_address}
                  datePrefix="Delivery"
                  taxId={shippingData.delivery_address?.tax_id}
                  timeSlotLabel="Delivery Time Slot"
                  timeSlot={shippingData.delivery_address?.delivery_time_slot}
                  notesLabel="Delivery Notes"
                  notes={shippingData.delivery_address?.delivery_notes}
                  locationTypeLabel="Location Type"
                  locationType={shippingData?.delivery_address?.location_type}
                  forkliftLabel="Forklift Available"
                  forklift={
                    shippingData?.delivery_address?.forklift_availability
                  }
                  assistanceLabel="Assistance Required"
                  assistance={
                    shippingData?.delivery_address?.no_assistance_at_delivery
                  }
                  startDate={shippingData?.delivery_address?.start_date}
                  endDate={shippingData?.delivery_address?.end_date}
                />

                <SectionDivider />
              </>
            )}

            {/* THIRD PARTY */}
            {shippingMode === "third_party" && (
              <>
                <AddressBlock
                  title="Pickup Address"
                  address={shippingData.pickup_address}
                  datePrefix="Pickup"
                  timeSlotLabel="Pickup Time Slot"
                  timeSlot={shippingData.pickup_address?.pickup_time_slot}
                  notesLabel="Pickup Notes"
                  notes={shippingData.pickup_address?.pickup_notes}
                  locationTypeLabel="Location Type"
                  locationType={shippingData?.pickup_address?.location_type}
                  forkliftLabel="Forklift Available"
                  forklift={shippingData?.pickup_address?.forklift_available}
                  assistanceLabel="Assistance Required"
                  assistance={shippingData?.pickup_address?.assistance_required}
                  startDate={shippingData?.pickup_address?.start_date}
                  endDate={shippingData?.pickup_address?.end_date}
                />

                <SectionDivider />

                <AddressBlock
                  title="Delivery Address"
                  address={shippingData.delivery_address}
                  datePrefix="Delivery"
                  taxId={shippingData.delivery_address?.tax_id}
                  timeSlotLabel="Delivery Time Slot"
                  timeSlot={shippingData.delivery_address?.delivery_time_slot}
                  notesLabel="Delivery Notes"
                  notes={shippingData.delivery_address?.delivery_notes}
                  locationTypeLabel="Location Type"
                  locationType={shippingData?.delivery_address?.location_type}
                  forkliftLabel="Forklift Available"
                  forklift={shippingData?.delivery_address?.forklift_available}
                  assistanceLabel="Assistance Required"
                  assistance={
                    shippingData?.delivery_address?.assistance_required
                  }
                  startDate={shippingData?.delivery_address?.start_date}
                  endDate={shippingData?.delivery_address?.end_date}
                />

                <SectionDivider />
              </>
            )}

            {/* BILLING ADDRESS (ALWAYS) */}
            <AddressBlock
              title="Billing Address"
              address={shippingData.billing_address}
              landmarkLabel="Landmark"
              landmark={shippingData.billing_address?.landmark}
              taxId={shippingData.billing_address?.tax_id}
            />
          </>
        )}
      </div>

      {/* <div style={sectionCard}>
              <h5 style={sectionTitle}>Trade & Delivery</h5>
              <Row>
                <Col md={6}>
                  <div style={infoRow}>
                    <strong>Incoterms:</strong> {buyer_product.incoterms}
                  </div>
                  <div style={infoRow}>
                    <strong>Payment Terms:</strong> {buyer_product.payment_terms}
                  </div>
                </Col>
                <Col md={6}>
                  <div style={infoRow}>
                    <strong>Lead Time:</strong> {buyer_product.lead_time} days
                  </div>
                  <div style={infoRow}>
                    <strong>Delivery Port:</strong>{" "}
                    {buyer_product.delivery_port || "N/A"}
                  </div>
                </Col>
              </Row>
            </div> */}

      {/* Third-Party Inspection Request */}
      <div style={sectionCard}>
        <h5 style={sectionTitle}>Third-Party Inspection (TPI)</h5>

        <Alert variant="info">
          Requesting TPI will initiate an inspection by a third-party agency.
          This action cannot be undone once submitted.
        </Alert>

        <Form.Check
          type="checkbox"
          id="tpi-request"
          label="Request Third-Party Inspection"
          checked={tpiRequested}
          disabled={tpiRequested || tpiLoading}
          onChange={handleTpiRequest}
        />

        {tpiRequested && (
          <div style={{ marginTop: 8, fontSize: "0.85rem", color: "#15803d" }}>
            ✔ TPI request has been successfully submitted
          </div>
        )}
      </div>

      {/* Extra Delivery Charge*/}
      {extra_delivery && (
        <div style={sectionCard}>
          <h5 style={sectionTitle}>Extra Delivery Charge</h5>
          <Row>
            <Col md={6}>
              <div style={infoRow}>
                <strong>Actual Delivery Distance:</strong> {CURRENCY}{" "}
                {extra_delivery?.distance_km}
              </div>
              <div style={infoRow}>
                <strong>Allowed Delivery Radius:</strong>{" "}
                {extra_delivery?.allowed_radius_km}
              </div>
              <div style={infoRow}>
                <strong>Extra Delivery Charge:</strong> {CURRENCY}{" "}
                {extra_delivery?.extra_delivery_charge}
              </div>
            </Col>
          </Row>
        </div>
      )}

      {/* Billing Summary */}
      <div style={sectionCard}>
        <h5 style={sectionTitle}>Billing Summary</h5>
        <Row>
          <Col md={6}>
            <div style={infoRow}>
              <strong>Final Amount:</strong>
              {formatCurrency(order_price_summary?.final_bid_price)}
              {/* {CURRENCY}{" "}
              {order_price_summary?.final_bid_price} */}
            </div>
            <div style={infoRow}>
              <strong>Tax Percentage:</strong>{" "}
              {order_price_summary?.tax_percentage}%
            </div>
            <div style={infoRow}>
              <strong>Tax Amount:</strong>
              {formatCurrency(order_price_summary?.tax_amount)}
              {/* {CURRENCY}{" "}
              {order_price_summary?.tax_amount} */}
            </div>
            <div style={infoRow}>
              <strong>Extra Delivery Charge:</strong>
              {formatCurrency(order_price_summary?.extra_delivery_charge)}
              {/* {CURRENCY}{" "}
              {order_price_summary?.extra_delivery_charge} */}
            </div>
          </Col>
          <Col md={6}>
            <div
              style={{
                ...infoRow,
                fontSize: "1.05rem",
                fontWeight: 600,
                color: theme.palette.success.main,
                marginTop: 12,
              }}
            >
              Total Payable: {formatCurrency(order_price_summary?.total_with_tax)}
              {/* {CURRENCY}{" "}
              {order_price_summary?.total_with_tax} */}
            </div>
          </Col>
        </Row>
      </div>

      {/* Disclaimer */}
      <div
        style={{
          marginBottom: 20,
          padding: "12px 14px",
          backgroundColor: "#fff8e1",
          border: "1px solid #ffe0b2",
          borderRadius: 6,
        }}
      >
        <Form.Check
          type="checkbox"
          id="checkout-disclaimer"
          checked={disclaimerAccepted}
          onChange={(e) => setDisclaimerAccepted(e.target.checked)}
          label={
            <span style={{ fontSize: "0.9rem", color: "#333" }}>
              {/* I confirm that all order, shipping, and payment details are
              correct and I agree to proceed with the transaction. I have read
              and accept the */}
              By using this platform, I agree to the{" "}
              <a
                href={TermsAndConditionsURL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: 500 }}
              >
                Terms & Conditions
              </a>
              .
            </span>
          }
        />
      </div>

      {/* Action Buttons */}
      <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
        <Button
          variant="outline-secondary"
          onClick={() => navigate("/dashboard")}
        >
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleProceed}
          disabled={paymentLoading || !disclaimerAccepted}
        >
          {paymentLoading ? "Processing..." : "Proceed"}
        </Button>
      </div>
    </Container>
  );
};

const SectionHeaderWithMeta = ({ title, mode }) => {
  if (!mode) return null;

  const shippingModeStyles = {
    self_pickup: { bg: "#ecfeff", color: "#155e75", border: "#67e8f9" },
    seller_ship: { bg: "#ecfdf5", color: "#065f46", border: "#6ee7b7" },
    third_party: { bg: "#fff7ed", color: "#9a3412", border: "#fdba74" },
  };

  const shippingModeMeta = {
    self_pickup: {
      label: "Self Pickup",
      icon: FaBoxOpen,
    },
    seller_ship: {
      label: "Seller Ship",
      icon: FaTruck,
    },
    third_party: {
      label: "Third Party",
      icon: FaHandshake,
    },
  };

  const style = shippingModeStyles[mode];
  const meta = shippingModeMeta[mode];
  const Icon = meta.icon;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 12,
      }}
    >
      <h5 style={sectionTitle}>{title}</h5>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 12px",
          borderRadius: 20,
          fontSize: "0.8rem",
          fontWeight: 500,
          backgroundColor: style.bg,
          color: style.color,
          border: `1px solid ${style.border}`,
          whiteSpace: "nowrap",
        }}
      >
        <Icon size={14} />
        <span>{meta.label}</span>
      </div>
    </div>
  );
};

const Field = ({ label, value }) => {
  if (!value) return null;

  return (
    <div style={{ marginBottom: 10 }}>
      <small style={{ color: "#666" }}>{label}</small>
      <div style={{ fontWeight: 500 }}>{value}</div>
    </div>
  );
};

const SectionDivider = () => (
  <div
    style={{
      height: 1,
      backgroundColor: "#e5e7eb",
      margin: "20px 0",
    }}
  />
);

const AddressBlock = ({
  title,
  address,

  timeSlotLabel,
  timeSlot,

  notesLabel,
  notes,

  forkliftLabel,
  forklift,

  assistanceLabel,
  assistance,

  locationTypeLabel,
  locationType,

  landmarkLabel,
  landmark,

  datePrefix,
  startDate,
  endDate,
  taxId,
}) => {
  if (!address) return null;

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString() : null;

  const formatYesNo = (value) => {
    if (value === 1) return "Yes";
    if (value === 0) return "No";
    return "N/A";
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <h4 style={{ marginBottom: 12, color: theme.palette.primary.main }}>
        {title}
      </h4>

      <Row>
        <Col md={3}>
          <Field
            label="Name"
            value={`${address.first_name} ${address.last_name}`}
          />
        </Col>

        <Col md={3}>
          <Field label="Phone" value={address.phone} />
        </Col>

        <Col md={3}>
          <Field label="Company" value={address.company_name} />
        </Col>

        {taxId && (
          <Col md={3}>
            <Field label="ABN/ACN/VAT/Tax ID" value={taxId} />
          </Col>
        )}

        <Col md={3}>
          <Field
            label="Address"
            value={`${address.address_line_1}${address.address_line_2 ? `, ${address.address_line_2}` : ""
              }`}
          />
        </Col>

        <Col md={3}>
          <Field label="City" value={address.city} />
        </Col>

        <Col md={3}>
          <Field label="State" value={address.state} />
        </Col>

        <Col md={3}>
          <Field label="Postcode" value={address.postcode} />
        </Col>

        {landmark && (
          <Col md={3}>
            <Field label={landmarkLabel} value={landmark} />
          </Col>
        )}

        {/* REQUIRED FIELDS */}
        {locationType && (
          <Col md={3}>
            <Field
              label={locationTypeLabel}
              value={capitalizeFirst(locationType)}
            />
          </Col>
        )}

        {forklift && (
          <Col md={3}>
            <Field
              label={forkliftLabel}
              value={
                forklift === 1 ? (
                  <Badge bg="success">Yes</Badge>
                ) : (
                  <Badge bg="warning">No</Badge>
                )
              }
            />
          </Col>
        )}

        {assistance && (
          <Col md={3}>
            <Field
              label={assistanceLabel}
              value={
                assistance === 1 ? (
                  <Badge bg="success">Yes</Badge>
                ) : (
                  <Badge bg="warning">No</Badge>
                )
              }
            />
          </Col>
        )}

        {startDate && (
          <Col md={3}>
            <Field
              label={`${datePrefix} Start Date`}
              value={formatDate(startDate)}
            />
          </Col>
        )}

        {endDate && (
          <Col md={3}>
            <Field
              label={`${datePrefix} End Date`}
              value={formatDate(endDate)}
            />
          </Col>
        )}

        {timeSlot && (
          <Col md={3}>
            <Field label={timeSlotLabel} value={timeSlot} />
          </Col>
        )}
      </Row>

      {/* NOTES SECTION — LAST ROW, 2-COLUMN GRID */}
      {(notes || address.no_assistance_at_pickup === 1) && (
        <Row style={{ marginTop: 12 }}>
          {notes && (
            <Col md={6}>
              <Field label={notesLabel} value={notes} />
            </Col>
          )}

          {address.no_assistance_at_pickup === 1 && (
            <Col md={6}>
              <Field
                label="Special Assistance Notes"
                value={address.special_assistance_notes}
              />
            </Col>
          )}
        </Row>
      )}
    </div>
  );
};

const VehicleInfoBlock = ({ vehicle }) => {
  if (!vehicle?.vehicle_type && !vehicle?.registration_no) return null;

  return (
    <>
      <h6>Vehicle Information</h6>
      <Field label="Vehicle Type" value={vehicle.vehicle_type} />
      <Field label="Registration No" value={vehicle.registration_no} />
    </>
  );
};

const DriverInfoBlock = ({ driver }) => {
  if (!driver?.name && !driver?.phone && !driver?.license) return null;

  return (
    <>
      <h6>Driver Information</h6>
      <Field label="Name" value={driver.name} />
      <Field label="Phone" value={driver.phone} />
      <Field label="License" value={driver.license} />
    </>
  );
};

// 💡 Address formatter
const renderAddress = (address) => {
  if (!address) return "N/A";

  const {
    name,
    address_line_1,
    address_line_2,
    city,
    state,
    postal_code,
    region,
  } = address;

  const parts = [
    name,
    address_line_1,
    address_line_2,
    city,
    state && postal_code ? `${state} - ${postal_code}` : state || postal_code,
    region,
  ].filter(Boolean); // removes undefined, null, or ""

  return parts.join(", ");
};

const getStatusColor = (status) => {
  switch (status.toLowerCase()) {
    case "pending":
      return "warning";
    case "confirmed":
      return "success";
    case "cancelled":
      return "danger";
    case "rejected":
      return "secondary";
    default:
      return "info";
  }
};

// Styles
const sectionCard = {
  backgroundColor: "#fff",
  padding: 24,
  borderRadius: 16,
  boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
  marginBottom: 24,
};

const sectionTitle = {
  fontWeight: 600,
  marginBottom: 16,
};

const infoRow = {
  fontSize: "0.95rem",
  marginBottom: 10,
};

const textMuted = {
  fontSize: "0.9rem",
  color: "#666",
};

export default OrderSummary;
